import { h } from '@omni-torn/ui';
import { hiddenByLevel, mayUseOmniTorn, NO_OVERRIDES, NOT_OPEN_TEXT, sectionByPath, sectionState, SECTION_GROUPS, WEB_SECTIONS, type Viewer, type WebSection } from '@omni-torn/shared';
import { session } from './session';
import { box, keyMakeLink, lockIcon } from './ui';
import { ownerBadge } from './pages/owner';

/**
 * The page around every section: the side menu (greyed out with a padlock where your key or
 * level doesn't reach, with a line saying what's needed), who you are at the top, and the
 * section itself.
 */

export type Page = (view: HTMLElement, path: string) => void | Promise<void>;

const LEVEL_LABEL: Record<string, string> = { owner: 'Owner', developer: 'Developer', alpha: 'Alpha', beta: 'Beta', exclusive: 'Exclusive', basic: 'Basic' };

export function viewer(): Viewer {
  const me = session.me();
  const s = session.get();
  return { key: s?.access ?? null, level: me?.level.level ?? 'basic', factionId: me?.factionId ?? null, access: me?.access ?? {}, mine: me?.mine ?? NO_OVERRIDES };
}

const nav = document.getElementById('sidenav') as HTMLElement;
const who = document.getElementById('who') as HTMLElement;
const menuButton = document.getElementById('menu-toggle') as HTMLButtonElement;
const layout = document.getElementById('layout') as HTMLElement;

function setMenuOpen(open: boolean): void {
  // On phones the menu slides in just under the top bar, wherever that is right now.
  if (open) nav.style.top = `${Math.max(0, (document.querySelector('.topbar') as HTMLElement).getBoundingClientRect().bottom)}px`;
  layout.classList.toggle('menu-open', open);
  menuButton.setAttribute('aria-expanded', String(open));
}
menuButton.addEventListener('click', () => setMenuOpen(!layout.classList.contains('menu-open')));
nav.addEventListener('click', (e) => {
  if ((e.target as Element).closest('a')) setMenuOpen(false);
});
document.addEventListener('keydown', (e) => e.key === 'Escape' && setMenuOpen(false));
window.addEventListener('scroll', () => layout.classList.contains('menu-open') && setMenuOpen(true), { passive: true });

export function drawShell(): void {
  const s = session.get();
  const me = session.me();
  if (!s) {
    nav.hidden = true;
    menuButton.hidden = true;
    who.replaceChildren();
    layout.classList.remove('signed-in');
    return;
  }
  nav.hidden = false;
  menuButton.hidden = false;
  layout.classList.add('signed-in');
  const level = me?.level.level ?? 'basic';
  who.replaceChildren(
    h('a', { href: '/account', 'data-link': true, class: 'who-link', title: 'Account & sync' }, h('span', { class: 'who-name' }, s.name || `Player ${s.playerId}`), h('span', { class: 'muted who-id' }, ` [${s.playerId}]`)),
    h('span', { class: `level-badge level-${level}`, title: 'Your Omni-Torn level' }, LEVEL_LABEL[level] ?? level),
  );
  drawNav();
}

export function drawNav(): void {
  const v = viewer();
  const here = sectionByPath(location.pathname)?.id;
  const groups = new Map<WebSection['group'], HTMLElement[]>();
  for (const sec of WEB_SECTIONS) {
    if (hiddenByLevel(sec, v)) continue;
    const st = sectionState(sec, v);
    const link = h(
      'a',
      { href: sec.path, 'data-link': true, class: `nav-item${here === sec.id ? ' active' : ''}${st.open ? '' : ' locked'}`, 'data-section': sec.id, title: st.open ? sec.blurb : st.text, ...(here === sec.id ? { 'aria-current': 'page' } : {}) },
      h('span', { class: 'nav-name' }, sec.name, st.open ? null : lockIcon()),
      st.open ? null : h('span', { class: 'nav-need' }, st.text),
    );
    const list = groups.get(sec.group) ?? [];
    list.push(link);
    groups.set(sec.group, list);
  }
  nav.replaceChildren(...[...groups].map(([g, links]) => h('div', { class: 'nav-group' }, h('div', { class: 'nav-group-title' }, SECTION_GROUPS[g]), ...links)));
  // The owner: a badge on Owner for notices about the Discord bot he hasn't seen (public bot G2).
  if (v.level === 'owner') ownerBadge(nav);
  const s = session.get();
  if (s?.access) {
    nav.append(
      h(
        'div',
        { class: 'nav-key' },
        h('span', { class: 'muted' }, 'Your key: '),
        h('strong', {}, s.access.custom ? 'Custom' : s.access.type),
        s.mode === 'extension' ? h('span', { class: 'muted' }, ' (via the extension)') : null,
        s.access.level < 4 && !s.access.custom ? h('div', {}, keyMakeLink(4, 'Full Access unlocks everything ↗')) : null,
      ),
    );
  }
}

/** The section's own page, or a lock screen explaining what it needs. */
export async function showSection(view: HTMLElement, pages: Record<string, Page>): Promise<void> {
  const sec = sectionByPath(location.pathname);
  drawNav();
  if (!sec) {
    document.title = 'Not found · Omni-Torn';
    view.replaceChildren(box('Not here', h('p', {}, 'There’s no page at this address. ', h('a', { href: '/', 'data-link': true }, 'Go to the overview'))));
    return;
  }
  document.title = `${sec.name} · Omni-Torn`;
  const st = sectionState(sec, viewer());
  if (!st.open) {
    view.replaceChildren(
      h(
        'section',
        { class: 'panel locked lock-page', 'data-locked': st.why },
        h('header', { class: 'panel-head' }, h('h2', {}, lockIcon(), ` ${sec.name}`)),
        h(
          'div',
          { class: 'panel-body' },
          h('p', {}, sec.blurb),
          h('p', { class: 'lock-text' }, st.text + '.'),
          st.why === 'key' ? h('p', {}, keyMakeLink(st.needs), ' — then ', h('a', { href: '/account#key', 'data-link': true }, 'add it in Account'), '. Your key only ever reads; Omni-Torn never changes anything in Torn.') : null,
          st.why === 'level' && !mayUseOmniTorn(viewer().level, viewer().access, viewer().mine)
            ? h('p', { class: 'muted not-open' }, NOT_OPEN_TEXT, ' ', h('a', { href: '/account', 'data-link': true }, 'Account'), ' has your key, tracking and your data.')
            : st.why === 'level'
              ? h('p', { class: 'muted' }, 'Sections are opened up level by level. It’ll appear here when it’s available to you.')
              : null,
        ),
      ),
    );
    return;
  }
  const page = pages[sec.id];
  if (!page) return;
  view.replaceChildren();
  await page(view, location.pathname);
}
